import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AccountProvider } from "@/contexts/AccountContext";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Upload from "@/pages/Upload";
import Settings from "@/pages/Settings";
import PublicSnapshot from "@/pages/PublicSnapshot";
import Admin from "@/pages/Admin";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F4]">
        <div className="font-mono-data text-sm text-[#6E716C]">Checking session…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function NonAdminRoute({ children }) {
  const { user, isAdmin, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F4]">
        <div className="font-mono-data text-sm text-[#6E716C]">Checking session…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (isAdmin) return <Navigate to="/admin" replace />;
  return children;
}

function LandingOrAdmin() {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return null;
  if (user && isAdmin) return <Navigate to="/admin" replace />;
  return <Login />;
}

function AppRouter() {
  const location = useLocation();
  // Handle #session_id fragment before any other route logic
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingOrAdmin />} />
      <Route path="/s/:share_id" element={<PublicSnapshot />} />
      <Route
        path="/dashboard"
        element={
          <NonAdminRoute>
            <Dashboard />
          </NonAdminRoute>
        }
      />
      <Route
        path="/upload"
        element={
          <NonAdminRoute>
            <Upload />
          </NonAdminRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <NonAdminRoute>
            <Settings />
          </NonAdminRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AccountProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "#FFFFFF",
                  border: "1px solid #E5E5DE",
                  borderRadius: 0,
                  fontFamily: "Manrope, sans-serif",
                  color: "#1A1B1A",
                },
              }}
            />
          </BrowserRouter>
        </AccountProvider>
      </AuthProvider>
    </div>
  );
}
